import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { scrollToSection } from '../../../utils/scroll';
import { handleSectionNavigation } from '../../../utils/navigation';
import { IoMdArrowForward } from 'react-icons/io';
import { MdDarkMode, MdLightMode } from 'react-icons/md';
import { HiInformationCircle } from 'react-icons/hi';
import {
  FiHome,
  FiBarChart2,
  FiBriefcase,
  FiUsers,
  FiPhone,
  FiChevronDown
} from 'react-icons/fi';
import logo from '../../../assets/images/logo.png';
import './Header.css';

export const MOBILE_MENU_EVENT = 'appMobileMenuChange';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState('light');
  const [pendingSection, setPendingSection] = useState(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');

    const initialTheme =
      savedTheme === 'light' || savedTheme === 'dark'
        ? savedTheme
        : 'light';

    setTheme(initialTheme);

    document.documentElement.setAttribute(
      'data-theme',
      initialTheme
    );
  }, []);

  useEffect(() => {
    document.body.classList.toggle(
      'mobile-menu-open',
      isMenuOpen
    );

    window.dispatchEvent(
      new CustomEvent(MOBILE_MENU_EVENT, {
        detail: {
          open: isMenuOpen
        }
      })
    );

    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;

      setScrolled(isScrolled);

      if (window.innerWidth >= 768) {
        document.body.style.paddingTop = isScrolled
          ? '70px'
          : '90px';
      }
    };

    const handleResize = () => {
      setIsDropdownOpen(null);

      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
        setOpenMobileDropdown(null);

        document.body.style.paddingTop =
          window.scrollY > 10
            ? '70px'
            : '90px';
      } else {
        document.body.style.paddingTop = '';
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.body.style.paddingTop = '';
      document.body.classList.remove('mobile-menu-open');
    };
  }, []);

  useLayoutEffect(() => {
    const targetingSection =
      pendingSection &&
      pendingSection !== 'início';

    if (targetingSection) {
      setScrolled(true);

      if (window.innerWidth >= 768) {
        document.body.style.paddingTop = '70px';
      } else {
        document.body.style.paddingTop = '';
      }

      return;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto'
    });

    setScrolled(false);

    if (window.innerWidth >= 768) {
      document.body.style.paddingTop = '90px';
    } else {
      document.body.style.paddingTop = '';
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      location.pathname !== '/' ||
      !pendingSection
    ) {
      return;
    }

    const section = pendingSection;

    setPendingSection(null);

    let attempts = 0;
    let frameId;

    const tryScroll = () => {
      const cleanId = section.startsWith('/')
        ? section.substring(1)
        : section;

      const target = document.getElementById(cleanId);

      if (
        target ||
        section === 'início' ||
        attempts >= 40
      ) {
        scrollToSection(section, false, true);
        return;
      }

      attempts += 1;
      frameId = requestAnimationFrame(tryScroll);
    };

    frameId = requestAnimationFrame(tryScroll);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [location.pathname, pendingSection]);

  const toggleTheme = () => {
    setTheme((currentTheme) => {
      const newTheme =
        currentTheme === 'dark'
          ? 'light'
          : 'dark';

      document.documentElement.setAttribute(
        'data-theme',
        newTheme
      );

      localStorage.setItem(
        'theme',
        newTheme
      );

      return newTheme;
    });
  };

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setOpenMobileDropdown(null);
  };

  const handleNavigation = (e, id) => {
    const isHomePage =
      window.location.pathname === '/' ||
      window.location.pathname === '';

    setIsDropdownOpen(null);
    setOpenMobileDropdown(null);

    if (!isHomePage) {
      setPendingSection(id);
    }

    handleSectionNavigation(
      e,
      id,
      navigate,
      scrollToSection
    );
  };

  const handleMobileNavigation = (e, id) => {
    closeMobileMenu();
    handleNavigation(e, id);
  };

  const toggleMobileDropdown = (id) => {
    setOpenMobileDropdown((current) =>
      current === id ? null : id
    );
  };

  const toggleDesktopDropdown = (id) => {
    setIsDropdownOpen((current) =>
      current === id ? null : id
    );
  };

  const navItems = [
    {
      id: 'início',
      label: 'Início',
      icon: <FiHome />
    },
    {
      id: 'operaçoes',
      label: 'Operações',
      icon: <FiBriefcase />,
      dropdown: [
        {
          id: 'serviços',
          label: 'Consignado'
        },
        {
          id: 'simulador',
          label: 'Simular portabilidade'
        }
      ]
    },
    {
      id: 'grupo-egel',
      label: 'Grupo Egel',
      icon: <FiUsers />,
      dropdown: [
        {
          id: 'sobre',
          label: 'Sobre o Grupo Egel'
        },
        {
          id: 'nossas-redes',
          label: 'Nossas redes',
          url: 'https://linktr.ee/grupo.egel'
        },
        {
          id: 'serviços-faq',
          label: 'Perguntas frequentes'
        },
        {
          id: 'contato',
          label: 'Entre em contato'
        },
        {
          id: 'termos-servico',
          label: 'Termos de serviço',
          route: '/termos-servico'
        }
      ]
    },
    {
      id: 'parceiros',
      label: 'Parceiros',
      icon: <FiUsers />,
      dropdown: [
        {
          id: 'parceiros',
          label: 'Nossos parceiros'
        },
        {
          id: 'parceria',
          label: 'Seja parceiro',
          route: '/parceiros'
        }
      ]
    }
  ];

  return (
    <>
      <div className="important-bar">
        <div className="important-bar-content">
          <HiInformationCircle className="important-bar-icon" />

          <span>
            <strong>IMPORTANTE:</strong>&nbsp;
            Nunca solicitamos depósitos antecipados. Prestamos serviço GRATUITO.
          </span>
        </div>
      </div>

      <header
        id="header"
        className={`header ${scrolled ? 'scrolled' : ''}`}
      >
        <div className="header-container">
          <div className="header-inner">
            <a
              className="logo"
              href="#início"
              onClick={(e) =>
                handleNavigation(e, 'início')
              }
            >
              <div className="icon">
                <img
                  src={logo}
                  alt="Grupo Egel Logo"
                  className="logo-img"
                />
              </div>

              <div className="text">
                <h1>Grupo Egel</h1>
                <p>Empresas Crefins e Egel</p>
              </div>
            </a>

            <nav className="desktop-nav">
              <ul>
                {navItems.map((item) => (
                  <li
                    key={item.id}
                    className={
                      item.dropdown &&
                        isDropdownOpen === item.id
                        ? 'has-dropdown dropdown-open'
                        : item.dropdown
                          ? 'has-dropdown'
                          : ''
                    }
                    onMouseEnter={() => {
                      if (item.dropdown) {
                        setIsDropdownOpen(item.id);
                      } else {
                        setIsDropdownOpen(null);
                      }
                    }}
                    onMouseLeave={() => {
                      if (item.dropdown) {
                        setIsDropdownOpen(null);
                      }
                    }}
                  >
                    {item.dropdown ? (
                      <button
                        type="button"
                        className="nav-link nav-dropdown-trigger"
                        onClick={() =>
                          toggleDesktopDropdown(item.id)
                        }
                        aria-expanded={
                          isDropdownOpen === item.id
                        }
                      >
                        {item.label}

                        <FiChevronDown className="dropdown-chevron" />

                        <span className="underline"></span>
                      </button>
                    ) : (
                      <a
                        href={`#${item.id}`}
                        className="nav-link"
                        onClick={(e) =>
                          handleNavigation(
                            e,
                            item.id
                          )
                        }
                      >
                        {item.label}

                        <span className="underline"></span>
                      </a>
                    )}

                    {item.dropdown && (
                      <div className="nav-dropdown">
                        {item.dropdown.map(
                          (subItem) => (
                            <a
                              key={subItem.id}
                              href={subItem.url || subItem.route || `#${subItem.id}`}
                              target={subItem.url ? '_blank' : undefined}
                              rel={subItem.url ? 'noopener noreferrer' : undefined}
                              className="nav-dropdown-link"
                              onClick={(e) => {
                                if (subItem.url) {
                                  setIsDropdownOpen(null);
                                  return;
                                }

                                if (subItem.route) {
                                  e.preventDefault();

                                  setIsDropdownOpen(null);
                                  navigate(subItem.route);

                                  return;
                                }

                                handleNavigation(e, subItem.id);
                              }}
                            >
                              <span>
                                {subItem.label}
                              </span>

                              <IoMdArrowForward className="nav-dropdown-arrow" />
                            </a>
                          )
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </nav>

            <div className="header-actions">
              <button
                type="button"
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label={
                  theme === 'dark'
                    ? 'Ativar modo claro'
                    : 'Ativar modo escuro'
                }
                title={
                  theme === 'dark'
                    ? 'Modo claro'
                    : 'Modo escuro'
                }
              >
                {theme === 'dark' ? (
                  <MdLightMode />
                ) : (
                  <MdDarkMode />
                )}
              </button>

              <button
                type="button"
                className={`mobile-toggle ${isMenuOpen ? 'active' : ''
                  }`}
                onClick={() =>
                  setIsMenuOpen(
                    (prev) => !prev
                  )
                }
                aria-label={
                  isMenuOpen
                    ? 'Fechar menu'
                    : 'Abrir menu'
                }
                aria-expanded={isMenuOpen}
              >
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`mobile-overlay ${isMenuOpen ? 'active' : ''
          }`}
        onClick={closeMobileMenu}
      />

      <div
        className={`mobile-menu ${isMenuOpen ? 'open' : ''
          }`}
      >
        <div className="mobile-menu-content">
          <button
            type="button"
            className="theme-toggle mobile"
            onClick={toggleTheme}
            aria-label={
              theme === 'dark'
                ? 'Ativar modo claro'
                : 'Ativar modo escuro'
            }
            title={
              theme === 'dark'
                ? 'Modo claro'
                : 'Modo escuro'
            }
          >
            <span className="icon">
              {theme === 'dark' ? (
                <MdLightMode />
              ) : (
                <MdDarkMode />
              )}
            </span>

            <span>
              {theme === 'dark'
                ? 'Modo claro'
                : 'Modo escuro'}
            </span>
          </button>

          <nav className="mobile-nav">
            <ul>
              {navItems.map((item) => (
                <li key={item.id}>
                  {item.dropdown ? (
                    <>
                      <button
                        type="button"
                        className="mobile-nav-link mobile-dropdown-toggle"
                        onClick={() =>
                          toggleMobileDropdown(
                            item.id
                          )
                        }
                        aria-expanded={
                          openMobileDropdown ===
                          item.id
                        }
                      >
                        <span className="nav-icon">
                          {item.icon}
                        </span>

                        <span className="nav-text">
                          {item.label}
                        </span>

                        <FiChevronDown
                          className={`mobile-dropdown-chevron ${openMobileDropdown ===
                              item.id
                              ? 'open'
                              : ''
                            }`}
                        />
                      </button>

                      <div
                        className={`mobile-submenu ${openMobileDropdown ===
                            item.id
                            ? 'open'
                            : ''
                          }`}
                      >
                        {item.dropdown.map(
                          (subItem) => (
                            <a
                              key={subItem.id}
                              href={subItem.url || subItem.route || `#${subItem.id}`}
                              target={subItem.url ? '_blank' : undefined}
                              rel={subItem.url ? 'noopener noreferrer' : undefined}
                              className="mobile-submenu-link"
                              onClick={(e) => {
                                if (subItem.url) {
                                  closeMobileMenu();
                                  return;
                                }

                                if (subItem.route) {
                                  e.preventDefault();

                                  closeMobileMenu();
                                  navigate(subItem.route);

                                  return;
                                }

                                handleMobileNavigation(e, subItem.id);
                              }}
                            >
                              <span>
                                {subItem.label}
                              </span>

                              <IoMdArrowForward className="arrow-icon" />
                            </a>
                          )
                        )}
                      </div>
                    </>
                  ) : (
                    <a
                      href={`#${item.id}`}
                      className="mobile-nav-link"
                      onClick={(e) =>
                        handleMobileNavigation(
                          e,
                          item.id
                        )
                      }
                    >
                      <span className="nav-icon">
                        {item.icon}
                      </span>

                      <span className="nav-text">
                        {item.label}
                      </span>

                      <IoMdArrowForward className="arrow-icon" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;